import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { API, BACKEND_URL } from "../App";
import { Heart, ArrowLeft } from "lucide-react";

export default function GratitudePage() {
  const { slug } = useParams();
  const [wedding, setWedding] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchWedding = async () => {
      try {
        const response = await axios.get(`${API}/weddings/slug/${slug}`);
        setWedding(response.data);
      } catch (err) {
        setError("Wedding not found");
      } finally {
        setLoading(false);
      }
    };
    fetchWedding();
  }, [slug]);

  const getMediaUrl = (url) => {
    if (!url) return null;
    return url.startsWith("http") ? url : `${BACKEND_URL}${url}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-wedding-black flex items-center justify-center">
        <div className="w-12 h-12 border-2 border-wedding-gold border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error || !wedding) {
    return (
      <div className="min-h-screen bg-wedding-black flex items-center justify-center px-6">
        <div className="text-center">
          <Heart className="w-16 h-16 text-wedding-gold/30 mx-auto mb-6" />
          <h1 className="font-serif text-3xl text-wedding-ivory mb-4">{error || "Not Found"}</h1>
          <Link to="/" className="btn-secondary">Return Home</Link>
        </div>
      </div>
    );
  }

  const coupleName = `${wedding.couple_name_1} & ${wedding.couple_name_2}`;

  return (
    <div className="min-h-screen bg-wedding-black" data-testid="gratitude-page">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-wedding-black/80 backdrop-blur-lg border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to={`/${slug}`} className="flex items-center gap-2 text-wedding-ivory/60 hover:text-wedding-gold transition-colors">
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">Back to Wedding</span>
          </Link>
          <div className="font-serif text-wedding-gold">MemoraOrbs</div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-24 pb-16 px-6">
        <div className="max-w-4xl mx-auto">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Heart className="w-12 h-12 text-wedding-gold mx-auto mb-6" />
            <p className="text-wedding-gold text-sm tracking-[0.3em] uppercase mb-4">With Love</p>
            <h1 className="font-serif text-4xl md:text-5xl text-wedding-ivory mb-4">Thank You</h1>
            <p className="text-wedding-ivory/60 text-lg">A message from {coupleName}</p>
          </motion.div>

          {/* Gratitude Video */}
          {wedding.gratitude_video && (
            <motion.div 
              className="video-container rounded-sm overflow-hidden mb-12"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <video
                src={getMediaUrl(wedding.gratitude_video)}
                controls
                className="w-full h-full"
              />
            </motion.div>
          )}

          {/* Gratitude Text */}
          {wedding.gratitude_text && (
            <motion.div 
              className="text-center"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <blockquote className="font-accent text-2xl md:text-3xl text-wedding-ivory/90 italic leading-relaxed max-w-2xl mx-auto">
                "{wedding.gratitude_text}"
              </blockquote>
              <p className="text-wedding-gold mt-8">— {coupleName}</p>
            </motion.div>
          )}

          {/* CTA */}
          <motion.div 
            className="text-center mt-16"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            <Link 
              to={`/${slug}`}
              className="btn-secondary inline-flex items-center gap-2"
            >
              View Full Wedding Page
              <ArrowLeft className="w-4 h-4 rotate-180" />
            </Link>
          </motion.div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 border-t border-white/5">
        <div className="text-center">
          <p className="text-wedding-gold/60 text-sm">MemoraOrbs</p>
          <p className="text-wedding-ivory/30 text-xs italic mt-1">Memories You Can Hold</p>
        </div>
      </footer>
    </div>
  );
}
